import flatpickr from 'flatpickr';
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';

/**
 * Flatpickr bootstrap + Korean locale + skmFlatpickr 헬퍼.
 *
 * 시리즈 B (2026-05-19, CLAUDE.md §UI/UX #4-B): bootstrap-datepicker npm 의존성은
 * Bootstrap 5 전환 단계에서 제거됨. 사용자 코드 `.datepicker(` 호출이 0건임을
 * 확인한 후 `$.fn.datepicker` 흡수 부분은 영구 제거.
 *
 * 현재 책임 — flatpickr 부트스트랩 인프라 (핵심):
 *   1. window.flatpickr / window.monthSelectPlugin 전역 노출
 *   2. flatpickr Korean locale 적용
 *   3. window.skmFlatpickr 헬퍼 객체 (init/initDay/initMonth/getDate/setDate/open/close)
 *
 * 사용처:
 *   - layouts/app.blade.php — initSkmFlatpickrInput(this) 가 window.flatpickr 직접 호출
 *   - 화면별 skm-datepicker class markup — window.skmFlatpickr.* 헬퍼 호출
 *   - skm-month-nav__input — window.monthSelectPlugin 직접 사용
 */

window.flatpickr = window.flatpickr || flatpickr;
window.monthSelectPlugin = window.monthSelectPlugin || monthSelectPlugin;

if (window.flatpickr) {
    window.flatpickr.localize(Korean);
}

function resolveInput(target) {
    if (!target) {
        return null;
    }

    if (target.jquery) {
        return target[0] || null;
    }

    if (typeof target === 'string') {
        return document.querySelector(target);
    }

    return target;
}

function buildDirectConfig(options = {}) {
    const config = {
        dateFormat: options.dateFormat || 'Y-m-d',
        disableMobile: true,
        onChange: options.onChange,
    };

    if (options.appendTo) {
        const appendTo = typeof options.appendTo === 'string'
            ? document.querySelector(options.appendTo)
            : options.appendTo;

        if (appendTo) {
            config.appendTo = appendTo;
        }
    }

    if (options.month) {
        config.plugins = [
            monthSelectPlugin({
                shorthand: false,
                dateFormat: config.dateFormat,
                altFormat: config.dateFormat,
            }),
        ];
    }

    return config;
}

function initDirect(target, options = {}) {
    const input = resolveInput(target);
    if (!input || !window.flatpickr) {
        return null;
    }

    if (input._flatpickr) {
        if (!options.reinit) {
            return input._flatpickr;
        }

        input._flatpickr.destroy();
    }

    return window.flatpickr(input, buildDirectConfig(options));
}

window.skmFlatpickr = window.skmFlatpickr || {
    init: initDirect,
    initDay(target, options = {}) {
        return initDirect(target, {...options, dateFormat: options.dateFormat || 'Y-m-d'});
    },
    initMonth(target, options = {}) {
        return initDirect(target, {...options, month: true, dateFormat: options.dateFormat || 'Y년 m월'});
    },
    getDate(target) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        return instance && instance.selectedDates ? (instance.selectedDates[0] || null) : null;
    },
    setDate(target, value, triggerChange = true) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        if (instance) {
            instance.setDate(value || null, triggerChange);
            return;
        }

        if (input) {
            input.value = value || '';
            if (triggerChange) {
                input.dispatchEvent(new Event('change', {bubbles: true}));
            }
        }
    },
    open(target) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        if (instance) {
            instance.open();
        }
    },
    close(target) {
        const input = resolveInput(target);
        const instance = input && input._flatpickr;

        if (instance) {
            instance.close();
        }
    },
};
